/**
 * Output Pipeline — 12 shared agents appended to every template
 * Defined once, imported by all template files. No code duplication.
 */

export const OUTPUT_PIPELINE_AGENTS = [
  { id: "OP_001", type: "orchestrator", label: "Synthesis Orchestrator", role: "synthesis_orchestrator", preferred_model: "gpt-4o" },
  { id: "OP_002", type: "analyst",      label: "Data Synthesizer Alpha", role: "data_synthesizer", preferred_model: "gpt-4o" },
  { id: "OP_003", type: "analyst",      label: "Data Synthesizer Beta",  role: "data_synthesizer", preferred_model: "gpt-4o" },
  { id: "OP_004", type: "analyst",      label: "Data Synthesizer Gamma", role: "data_synthesizer", preferred_model: "gpt-4o" },
  { id: "OP_005", type: "writer",       label: "Executive Summary Writer", role: "narrative_writer", preferred_model: "claude-sonnet-4" },
  { id: "OP_006", type: "writer",       label: "Detailed Analysis Writer", role: "narrative_writer", preferred_model: "claude-sonnet-4" },
  { id: "OP_007", type: "writer",       label: "Recommendations Writer",   role: "narrative_writer", preferred_model: "claude-sonnet-4" },
  { id: "OP_008", type: "analyst",      label: "Data Visualization Designer", role: "visual_designer", preferred_model: "gpt-4o" },
  { id: "OP_009", type: "analyst",      label: "Infographic Designer",     role: "visual_designer", preferred_model: "gpt-4o" },
  { id: "OP_010", type: "analyst",      label: "Slide Layout Designer",    role: "visual_designer", preferred_model: "gpt-4o" },
  { id: "OP_011", type: "writer",       label: "Presentation Assembler",   role: "presentation_builder", preferred_model: "gpt-4o" },
  { id: "OP_012", type: "analyst",      label: "Action Item Extractor",    role: "action_extractor", preferred_model: "gpt-4o" },
];

// Internal pipeline DAG edges (from, to)
const INTERNAL_EDGES = [
  ["OP_001", "OP_002"], ["OP_001", "OP_003"], ["OP_001", "OP_004"],
  ["OP_002", "OP_005"], ["OP_003", "OP_005"], ["OP_004", "OP_005"],
  ["OP_002", "OP_006"], ["OP_003", "OP_006"], ["OP_004", "OP_006"],
  ["OP_002", "OP_008"], ["OP_003", "OP_008"], ["OP_004", "OP_008"],
  ["OP_002", "OP_012"], ["OP_003", "OP_012"], ["OP_004", "OP_012"],
  ["OP_001", "OP_010"],
  ["OP_001", "OP_009"], ["OP_005", "OP_009"],
  ["OP_002", "OP_007"], ["OP_003", "OP_007"], ["OP_004", "OP_007"], ["OP_012", "OP_007"],
  ["OP_005", "OP_011"], ["OP_006", "OP_011"], ["OP_007", "OP_011"],
  ["OP_008", "OP_011"], ["OP_009", "OP_011"], ["OP_010", "OP_011"],
];

/**
 * Append the 12 Output Pipeline agents to any template's nodes and edges.
 * @param {Array} nodes - existing template nodes
 * @param {Array} edges - existing template edges (either [from,to] arrays or {from,to} objects)
 * @returns {{ nodes: Array, edges: Array }} augmented nodes and edges
 */
export function appendOutputPipeline(nodes, edges) {
  // Find leaf nodes (nodes with no outgoing edge)
  const edgesNormalized = edges.map(e =>
    Array.isArray(e) ? { from: e[0], to: e[1] } : e
  );
  const nodeIds = new Set(nodes.map(n => n.id));
  const sources = new Set(edgesNormalized.map(e => e.from));
  const leafIds = [...nodeIds].filter(id => !sources.has(id));

  // Position OP nodes below existing canvas
  const maxY = Math.max(...nodes.map(n => n.y || 0), 0);
  const baseY = maxY + 250;

  const xPos = {
    OP_001: 400, OP_002: 100, OP_003: 400, OP_004: 700,
    OP_005: 50,  OP_006: 250, OP_007: 450,
    OP_008: 650, OP_009: 200, OP_010: 500,
    OP_011: 400, OP_012: 750,
  };
  const yOff = {
    OP_001: 0,   OP_002: 130, OP_003: 130, OP_004: 130,
    OP_005: 260, OP_006: 260, OP_007: 390,
    OP_008: 260, OP_009: 390, OP_010: 260,
    OP_011: 520, OP_012: 260,
  };

  const opNodes = OUTPUT_PIPELINE_AGENTS.map(a => ({
    id: a.id,
    type: a.type,
    label: a.label,
    x: xPos[a.id] || 400,
    y: baseY + (yOff[a.id] || 0),
    preferred_model: a.preferred_model,
    op_role: a.role,
    is_output_pipeline: true,
  }));

  // Edges: leaf analyst nodes → OP_001
  const isArrayEdges = edges.length > 0 && Array.isArray(edges[0]);
  const leafEdges = leafIds.map(id =>
    isArrayEdges ? [id, "OP_001"] : { from: id, to: "OP_001" }
  );

  // Internal pipeline edges
  const pipelineEdges = INTERNAL_EDGES.map(([from, to]) =>
    isArrayEdges ? [from, to] : { from, to }
  );

  return {
    nodes: [...nodes, ...opNodes],
    edges: [...edges, ...leafEdges, ...pipelineEdges],
  };
}
